import React, { useEffect, useState } from "react";
import IfoodCatalogImportService from "../../../api/ifood_catalog_import";
import { dateMask } from "../../../common/dateMask";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CategoryIcon from "@mui/icons-material/Category";
import { styled } from "@mui/material/styles";
import { green, grey } from "@mui/material/colors";
import { ERROR_MESSAGE } from "../../../common/helpersTxt";
import CollapseAlert from "../../alerts/CollapseAlert";
import CircularProgress from "@mui/material/CircularProgress";
import DebouncedTextField from "../../Inputs/DebouncedTextField";
import { StyledGreyBadge } from "../../styled/badge";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Chip from "@mui/material/Chip";
import ContextAvailabilitySwitch from "./ContextAvailabilitySwitch";

// Styled components
const ColorIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.getContrastText(grey[500]),
  backgroundColor: grey[100],
  "&:hover": {
    backgroundColor: grey[200],
  },
}));

const ProductRow = styled(Stack)(({ theme }) => ({
  padding: 4,
  alignItems: "center",
  borderRadius: 5,
  backgroundColor: "#fff",
  justifyContent: "space-between",
  "&:hover": {
    backgroundColor: grey[200],
  },
}));

const CategoryAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 5,
  "&:before": {
    display: "none",
  },
}));

export default function ImportedItems({ ifood_id, editProduct }) {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [catalogData, setCatalogData] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [productsAvailability, setProductsAvailability] = useState({});
  const [updatingProducts, setUpdatingProducts] = useState({}); // Track products being updated
  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    getMerchantCatalogData();
  }, []);

  const getMerchantCatalogData = async () => {
    try {
      setLoading(true);
      const { data } =
        await IfoodCatalogImportService.getMerchantCatalogHierarchy(ifood_id);

      // Initialize availability state for each product and each context
      const initialAvailability = {};
      const initialUpdatingState = {};

      data.categories.forEach((category) => {
        category.products.forEach((product) => {
          initialAvailability[product.product_id] = {};
          initialUpdatingState[product.product_id] = {};

          // Initialize each context with the product's availability
          data.context.forEach((context) => {
            initialAvailability[product.product_id][context] =
              product.available;
            initialUpdatingState[product.product_id][context] = false;
          });
        });
      });

      setProductsAvailability(initialAvailability);
      setUpdatingProducts(initialUpdatingState);
      setCatalogData(data);
    } catch (e) {
      console.error(e);
      handleError(e.response?.data);
    } finally {
      setLoading(false);
    }
  };

  // Handle availability toggle for a specific product and context
  const handleAvailabilityToggle = async (
    productId,
    context,
    currentStatus
  ) => {
    const newStatus = !currentStatus;
    console.log(
      `Product ${productId} availability for context ${context} changed to: ${newStatus}`
    );

    try {
      // Mark this product-context combination as updating
      setUpdatingProducts((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [context]: true,
        },
      }));

      // First update local state for immediate feedback
      setProductsAvailability((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [context]: newStatus,
        },
      }));

      // Then call API to persist the change
      await IfoodCatalogImportService.updateProductAvailability(
        productId,
        ifood_id,
        context,
        newStatus
      );
    } catch (error) {
      // Revert local state if API call fails
      setProductsAvailability((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [context]: currentStatus, // Revert to original state
        },
      }));

      handleError(
        error.response?.data?.message || "Failed to update product availability"
      );
    } finally {
      // Clear updating state
      setUpdatingProducts((prev) => ({
        ...prev,
        [productId]: {
          ...prev[productId],
          [context]: false,
        },
      }));
    }
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  const handleCategoryExpand = (categoryId) => (event, isExpanded) => {
    setExpandedCategory(isExpanded ? categoryId : null);
  };

  const handleError = (message) => {
    setOpenErrorAlert(true);
    setErrorMessage(message ?? ERROR_MESSAGE);
  };

  // Filter products based on search query
  const getFilteredProducts = (products) => {
    if (!searchQuery) return products;
    return products.filter((product) =>
      product.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!catalogData) {
    return (
      <Typography variant="subtitle1" textAlign="center">
        Cardápio não encontrado, para importar é só clicar na nuvem acima!
      </Typography>
    );
  }

  return (
    <>
      <DebouncedTextField
        onSearch={handleSearchChange}
        placeHolder={"Nome do Item"}
      />

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Cardápio de {catalogData.merchant_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Última atualização: {dateMask(catalogData.updated_at)}
        </Typography>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {catalogData.context.map((context) => (
            <Chip
              key={context}
              label={context}
              size="small"
              variant="outlined"
              color="primary"
            />
          ))}
        </Box>
      </Box>

      {catalogData.categories.length === 0 ? (
        <Typography variant="subtitle1" textAlign="center">
          Nenhuma categoria encontrada neste cardápio.
        </Typography>
      ) : (
        catalogData.categories.map((category) => {
          const filteredProducts = getFilteredProducts(category.products);

          // Skip rendering categories with no matching products when searching
          if (searchQuery && filteredProducts.length === 0) {
            return null;
          }

          return (
            <CategoryAccordion
              key={category.id}
              expanded={expandedCategory === category.id}
              onChange={handleCategoryExpand(category.id)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`category-${category.id}-content`}
                id={`category-${category.id}-header`}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: category.color_hex || grey[200],
                      borderRadius: 1.5,
                      p: 0.75,
                      width: 36,
                      height: 36,
                    }}
                  >
                    <CategoryIcon fontSize="small" />
                  </Box>
                  <Typography fontWeight={600}>{category.title}</Typography>
                  <StyledGreyBadge>
                    <Typography variant="body2" color={grey["900"]}>
                      {filteredProducts.length}
                    </Typography>
                  </StyledGreyBadge>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {filteredProducts.length === 0 ? (
                  <Typography variant="body2" textAlign="center">
                    Nenhum produto encontrado nesta categoria.
                  </Typography>
                ) : (
                  filteredProducts.map((product, index) => (
                    <React.Fragment key={product.product_id}>
                      <ProductRow direction="row" spacing={2}>
                        <Box display="flex" alignItems="center">
                          <StyledGreyBadge>
                            <Typography variant="body2" color={grey["900"]}>
                              {index + 1}
                            </Typography>
                          </StyledGreyBadge>
                          <Box ml={1}>
                            <Typography>{product.title}</Typography>
                            {product.price && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                R$ {parseFloat(product.price).toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            pl: 1,
                            borderRadius: 5,
                            backgroundColor: grey["200"],
                          }}
                        >
                          {/* Context-specific Availability Switches */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            {catalogData.context.map((context) => (
                              <ContextAvailabilitySwitch
                                key={`${product.product_id}-${context}`}
                                productId={product.product_id}
                                context={context}
                                isAvailable={
                                  productsAvailability[product.product_id]?.[
                                    context
                                  ] || false
                                }
                                isUpdating={
                                  updatingProducts[product.product_id]?.[
                                    context
                                  ] || false
                                }
                                onToggle={handleAvailabilityToggle}
                              />
                            ))}
                          </Box>

                          <ColorIconButton onClick={() => editProduct(product)}>
                            {product.fiscal_code ? (
                              <CheckCircleIcon sx={{ color: green["500"] }} />
                            ) : (
                              <ReceiptIcon sx={{ color: grey["500"] }} />
                            )}
                          </ColorIconButton>
                        </Stack>
                      </ProductRow>
                      <Divider sx={{ my: 1 }} />
                    </React.Fragment>
                  ))
                )}
              </AccordionDetails>
            </CategoryAccordion>
          );
        })
      )}

      <CollapseAlert
        setOpenErrorAlert={setOpenErrorAlert}
        openErrorAlert={openErrorAlert}
        errorMessage={errorMessage}
      />
    </>
  );
}
